import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import api from 'api';
import {
    sortServicesByName,
    withDefaultRule,
    withoutServiceOverrides,
    withServiceOverrides,
} from 'helpers/serviceProviderBilling';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import type { AppDispatch } from 'redux/store';
import type {
    BillingRule,
    ServiceCountryCode,
    ServiceProviderBillingConfig,
    ServiceRecord,
} from 'types';
import { getErrorMessage } from 'utils/v2/errorHelper';

export function useServiceProviderBilling(
    providerId: number | null,
    countryCode: ServiceCountryCode | null
) {
    const dispatch = useDispatch<AppDispatch>();
    const { translate, language } = useTranslate();
    const [serviceList, setServiceList] = useState<ServiceRecord[]>([]);
    const [config, setConfig] = useState<ServiceProviderBillingConfig | null>(null);
    const [fetching, setFetching] = useState(false);
    const [loadedKey, setLoadedKey] = useState<string | null>(null);
    const requestKey =
        providerId == null || countryCode == null ? null : `${providerId}:${countryCode}`;
    // True on the first paint too, so an empty rule is not shown before the fetch.
    const loading = fetching || (requestKey != null && loadedKey !== requestKey);

    useEffect(() => {
        setServiceList([]);
        setConfig(null);
        if (providerId == null || countryCode == null) {
            setFetching(false);
            setLoadedKey(null);
            return;
        }
        const currentProviderId = providerId;
        const currentCountryCode = countryCode;
        let cancelled = false;
        const load = async () => {
            setFetching(true);
            try {
                const [visibleServices, billing] = await Promise.all([
                    api.services.listServices({
                        scope: 'visible',
                        service_provider_id: currentProviderId,
                        country_code: currentCountryCode,
                    }),
                    api.serviceProviderBilling.getBillingConfig({
                        service_provider_id: currentProviderId,
                        country_code: currentCountryCode,
                    }),
                ]);
                if (cancelled) {
                    return;
                }
                setServiceList(visibleServices.data || []);
                setConfig(billing.data);
            } catch (error) {
                if (!cancelled) {
                    dispatch(
                        setAlert({
                            message: getErrorMessage(
                                error,
                                translate('serviceProviderBilling.loadFailed')
                            ),
                            type: 'error',
                        })
                    );
                }
            } finally {
                if (!cancelled) {
                    setFetching(false);
                    setLoadedKey(`${currentProviderId}:${currentCountryCode}`);
                }
            }
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, [countryCode, dispatch, providerId, translate]);

    const services = useMemo(
        () => sortServicesByName(serviceList, language),
        [language, serviceList]
    );

    const save = async (
        buildConfig: (current: ServiceProviderBillingConfig) => ServiceProviderBillingConfig
    ): Promise<boolean> => {
        if (!config) {
            // Saving replaces every rule for the country, so it needs the rules that are saved now.
            dispatch(
                setAlert({ message: translate('serviceProviderBilling.loadFailed'), type: 'error' })
            );
            return false;
        }
        try {
            const response = await api.serviceProviderBilling.saveBillingConfig(
                buildConfig(config)
            );
            setConfig(response.data);
            dispatch(
                setAlert({ message: translate('serviceProviderBilling.saved'), type: 'success' })
            );
            return true;
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
            return false;
        }
    };

    const saveDefault = (rule: BillingRule) => save((current) => withDefaultRule(current, rule));

    const removeDefault = () => save((current) => withDefaultRule(current, null));

    const saveOverrides = (serviceIds: number[], rule: BillingRule) =>
        save((current) => withServiceOverrides(current, serviceIds, rule));

    const resetOverrides = (serviceIds: number[]) =>
        save((current) => withoutServiceOverrides(current, serviceIds));

    return {
        services,
        config,
        loading,
        saveDefault,
        removeDefault,
        saveOverrides,
        resetOverrides,
    };
}
